import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not(:disabled), [href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])';

// Module-level stack so that with nested dialogs (the item-detail drawer over
// the adder sheet) a single Escape closes only the topmost one, and the Tab
// trap is only enforced by whichever dialog is on top.
const dialogStack: symbol[] = [];

/**
 * Shared overlay-dialog keyboard behavior, matching the convention already used
 * by AttachmentLightbox/TicketModal: on open focus is moved into the dialog,
 * Escape closes (topmost only), Tab is trapped inside `rootRef`, focus is
 * restored to the trigger on unmount, and — for full-screen overlays — the body
 * scroll is optionally locked so iOS can't scroll-chain the page behind the
 * overlay.
 */
export function useModalA11y(
  rootRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  options: { lockScroll?: boolean; enabled?: boolean } = {},
) {
  const { lockScroll = false, enabled = true } = options;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!enabled) return undefined;
    const id = Symbol('dialog');
    dialogStack.push(id);
    const isTopmost = () => dialogStack[dialogStack.length - 1] === id;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move initial focus into the dialog so keyboard/screen-reader users land
    // inside the overlay (and the Tab trap below has something to anchor to),
    // rather than leaving focus on the trigger behind the modal. Prefer the
    // first focusable control; fall back to the dialog root itself.
    const root = rootRef.current;
    if (root && !root.contains(document.activeElement)) {
      const firstFocusable = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).find((el) => el.offsetParent !== null && !el.hasAttribute('aria-hidden'));
      if (firstFocusable) {
        firstFocusable.focus();
      } else {
        if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
        root.focus();
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTopmost()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key === 'Tab') {
        const focusable = Array.from(
          rootRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [],
        ).filter((el) => el.offsetParent !== null && !el.hasAttribute('aria-hidden'));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      const i = dialogStack.indexOf(id);
      if (i >= 0) dialogStack.splice(i, 1);
      previouslyFocused?.focus?.();
    };
  }, [rootRef, enabled]);

  useEffect(() => {
    if (!enabled || !lockScroll) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [lockScroll, enabled]);
}
